import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiUsers, FiMail, FiBookmark } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { IoSparklesOutline } from 'react-icons/io5';
import CandidateItem from '../candidates/candidateItem';
import Loader from '../../../common/loader';
import { RecruiterApi } from '../../../supports/recruiterApi';
import { generateRequestId } from '../../../common/common';
import { useToast } from '../../../common/toastSystem';
import unifiedAuthManager from '../../../services/unifiedAuthManager';

const FilterTab = ({ label, count, isActive, onClick, disabled }) => (
    <button
        onClick={onClick}
        className={`relative ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        disabled={disabled}
    >
      <span className={`font-semibold text-[0.8rem] ${isActive ? 'mph-primary' : 'text-gray-500'}`}>
        {label === 'Applicants'
  ? 'Job Applicants'
  : label === 'New'
  ? 'Recommended'
  : label === 'Recommended'
  ? 'Other Options' 
  : label}
      </span>
        {isActive && (
            <>
                {count > 0 && (
                    <span className="ml-2 text-xs font-bold mph-primary">{count}</span>
                )}
                <div className="absolute bottom-0 left-0 w-full h-0.5 bg-emerald-500 rounded-full"></div>
            </>
        )}
    </button>
);

const MatchingCandidatesList = ({
    pageType = 'candidates',
    onMatchingCandidateClick,
    candidates: candidatesProp,
    query,
    searchType,
    mph_jobid,
    job_id,
    jobDetails,
    candidateActiveTab,
    isFirst = 0,
    onUpdateCandidate
}) => {
    const [activeTab, setActiveTab] = useState(candidateActiveTab ? candidateActiveTab : 'New');
    const [submitting, setSubmitting] = useState(false);
    const [selectAll, setSelectAll] = useState(false);
    const [viewedIds, setViewedIds] = useState([]);
    const [taggedIds, setTaggedIds] = useState([]);
    const [appliededIds, setAppliedIds] = useState([]);
    const [archivedIds, setArchivedIds] = useState([]);
    const [loadingTab, setLoadingTab] = useState(false);
    const [candidates, setCandidates] = useState(candidatesProp || []);
    const [isFirstTime, setIsFirstTime] = useState(isFirst ? isFirst : 0);

    // Store fetched candidates only for tabs that make API calls
    const [fetchedArchived, setFetchedArchived] = useState([]);

    const toast = useToast();
    const user = unifiedAuthManager.getUnifiedUser();

    // 🔥 FIXED: Only update from props for tabs that don't fetch their own data
    useEffect(() => {
        const tabsThatFetchOwnData = ['Viewed', 'Tagged', 'Archived', 'Applied'];
        
        if (!tabsThatFetchOwnData.includes(activeTab)) {
            setCandidates(candidatesProp || []);
        }
    }, [candidatesProp, activeTab]);

    useEffect(() => {
        console.log('current activeTab:', activeTab);
        console.log('candidatesProp changed:', candidatesProp);
    }, [activeTab, candidates]);

    useEffect(() => {
        if (candidateActiveTab && candidateActiveTab !== activeTab) {
            setActiveTab(candidateActiveTab);
        }
    }, [candidateActiveTab]);

    useEffect(() => {
        console.log('isFirstTime:', activeTab);
        
        // Skip API calls for New, Recommended, and Applicants tabs
        if (['New', 'Recommended', 'Applicants'].includes(activeTab)) {
            console.log('Skipping API call for tab:', activeTab);
            setLoadingTab(false); // Ensure loading state is false
            return;
        }

        let cancelled = false;
        console.log('Fetching tab ids for:', activeTab);
        
        const fetchTabIds = async () => {
            if (!job_id) {
                console.log('Skipping fetch - no job_id');
                return;
            }

            setLoadingTab(true);

            const tabConfig = {
                Viewed: {
                    api: RecruiterApi.candidatesViewed,
                    setter: setViewedIds,
                    reqPayload: {
                        payload: { job_id },
                        user_payload: { request_id: generateRequestId() },
                    },
                },
                Tagged: {
                    api: RecruiterApi.candidatesTagged,
                    setter: setTaggedIds,
                    reqPayload: {
                        payload: { job_id },
                        user_payload: { request_id: generateRequestId() },
                    },
                },
                Archived: {
                    api: RecruiterApi.getSmartMatchCandidates,
                    setter: setArchivedIds,
                    reqPayload: {
                        user_payload: {
                            request_id: generateRequestId(),
                            email: user.email,
                            tenant: "",
                            user_id: user.id,
                        },
                        payload: {
                            job_id: job_id,
                            status: "archived",
                        },
                    },
                },
                Applied: {
                    api: RecruiterApi.candidatesApplied,
                    setter: setAppliedIds,
                    reqPayload: {
                        payload: { 
                            job_id: job_id,
                            mph_jobid: mph_jobid
                        },
                        user_payload: { request_id: generateRequestId() },
                    },
                },
            };

            if (!tabConfig[activeTab]) {
                setLoadingTab(false);
                return;
            }

            const { api, setter, reqPayload } = tabConfig[activeTab];

            try {
                const res = await api?.(reqPayload);
                
                if (activeTab === 'Archived') {
                    if (res?.success && res?.data?.hits) {
                        const archived = res.data.hits.filter(c => !c.status?.toLowerCase().includes("rejected"));
                        setFetchedArchived(archived);
                        setter(archived.map(c => c.id));
                    } else {
                        setter([]);
                        toast.warning('Failed to get candidates. Please try again later.');
                    }
                } else if (activeTab === 'Applied') {
                    if (res?.success && res?.data?.candidates) {
                        const cleaned = res.data.candidates.map(c => ({
                            ...c,
                            id: c.candidate_id
                        }));
                        
                        setter(cleaned.map(c => c.id));
                        setCandidates(prev => [...prev, ...cleaned]);
                    } else {
                        setter([]);
                        toast.warning('Failed to get candidates. Please try again later.');
                    }
                } else {
                    if (res?.success) {
                        setter(res.data.candidate_ids || []);
                    } else {
                        setter([]);
                        toast.warning('Failed to get candidates. Please try again later.');
                    }
                }
            } catch (e) {
                console.error('Error fetching tab data:', e);
                setter([]);
            } finally {
                setLoadingTab(false);
            }
        };

        // Only fetch for Viewed, Tagged, Archived, and Applied tabs
        if (['Viewed', 'Tagged', 'Archived', 'Applied'].includes(activeTab)) {
            fetchTabIds();
        }

        return () => { cancelled = true; };
    }, [activeTab, job_id]);

    const candidateMap = (candidates || []).reduce((acc, c) => {
        acc[c.id] = c;
        return acc;
    }, {});

    const candidatesByStatus = {
        Submitted: candidates?.filter(c => c.is_submitted) || [],
        Applied: candidates?.filter(c => c.Status === 'Applied') || [],
    };

    const getCandidatesByIds = (idList) =>
        Array.isArray(idList)
            ? idList.map(id => candidateMap[id]).filter(Boolean)
            : [];

    const submittedIds = new Set(candidatesByStatus.Submitted.map(c => c.id));
    const allOtherIds = new Set([
        ...viewedIds,
        ...taggedIds,
        ...submittedIds
    ]);

    // Filter candidates from candidatesProp based on match_type
    const candidatesNew = (candidates || [])
    .filter(c => !c.status?.toLowerCase().includes("rejected") && c.match_type === "recommended")
    .sort((a, b) => (b.llm_score || 0) - (a.llm_score || 0));

const candidatesRecommended = (candidates || [])
    .filter(c => !c.status?.toLowerCase().includes("rejected") && c.match_type === "related")
    .sort((a, b) => (b.llm_score || 0) - (a.llm_score || 0));

const candidatesApplied2 = (candidates || [])
    .filter(c => c.match_type === "applied")
    .sort((a, b) => (b.llm_score || 0) - (a.llm_score || 0));

const candidatesApplicants = (candidates || [])
    .filter(c => c.match_type === "applicant")
    .sort((a, b) => {
        // First sort by placement status
        const aIsPlaced = a.status?.toLowerCase().includes("placed");
        const bIsPlaced = b.status?.toLowerCase().includes("placed");
        if (aIsPlaced && !bIsPlaced) return -1;
        if (!aIsPlaced && bIsPlaced) return 1;
        
        // Then sort by llm_score (descending)
        return (b.llm_score || 0) - (a.llm_score || 0);
    });

    // Use candidatesProp data directly for New, Recommended, and Applicants tabs
    const tabCandidates = {
        New: candidatesNew,
        Applicants: candidatesApplicants,
        Recommended: candidatesRecommended,
        Viewed: getCandidatesByIds(viewedIds),
        Submitted: candidatesByStatus.Submitted,
        Tagged: getCandidatesByIds(taggedIds),
        Applied: candidatesApplied2,
        Archived: fetchedArchived.length > 0 ? fetchedArchived : getCandidatesByIds(archivedIds)
    };

    const filterCandidates = (candidates, activeTab) => {
        try {
            if (!Array.isArray(candidates)) return [];

            const today = new Date();

            return candidates.filter(c => {
                const status = (c?.crm_status || '').toLowerCase();
                if (status.includes('incomplete') || status.includes('blacklisted')) {
                    return false;
                }

                if (activeTab === 'New' && c?.is_submitted) {
                    return false;
                }

                if (activeTab === 'Archived') {
                    if (!c?.date_of_birth) return false;

                    const dob = new Date(c.date_of_birth);
                    let age = today.getFullYear() - dob.getFullYear();

                    const m = today.getMonth() - dob.getMonth();
                    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
                        age--;
                    }

                    if (age < 60) return false;
                }

                return true;
            });
        } catch (error) {
            console.error('Error while filtering candidates:', error);
            return [];
        }
    };

    const candidatesToShow = filterCandidates(tabCandidates[activeTab], activeTab);

    const tabs = [
        { label: 'New', count: tabCandidates.New.length },
        { label: 'Recommended', count: tabCandidates.Recommended.length },
        { label: 'Applicants', count: tabCandidates.Applicants.length },
        { label: 'Viewed', count: tabCandidates.Viewed.length },
        // { label: 'Submitted', count: tabCandidates.Submitted.length },
        { label: 'Tagged', count: tabCandidates.Tagged.length },
        { label: 'Applied', count: tabCandidates.Applied.length },
        { label: 'Archived', count: candidatesToShow.length }
    ];

    const handleSubmitTaged = () => {
        setSubmitting(true);
        setTimeout(() => {
            alert(`Submitted ${tabCandidates.Tagged.length} Tagged candidate(s) to AM!`);
            setSubmitting(false);
        }, 1000);
    };

    const handleMatchingCandidateClick = (candidate) => {
        onMatchingCandidateClick(candidate, activeTab);
    };

    const handleTagAction = useCallback((untaggedCandidateId, isTagged) => {
        if (untaggedCandidateId) {
            setTaggedIds(prev => prev.filter(id => id !== untaggedCandidateId));
            setCandidates(prevCandidates =>
                prevCandidates.map((cand) =>
                    cand.id === untaggedCandidateId
                        ? { ...cand, is_tagged: !isTagged }
                        : cand
                )
            );
        }
    }, [activeTab]);

    const handleSubmission = useCallback((submittedCandidateId) => {
        setCandidates(prevCandidates =>
            prevCandidates.map(c =>
                c.id === submittedCandidateId
                    ? { ...c, is_submitted: true }
                    : c
            )
        );
    }, []);

    if (!candidates) {
        return (
            <main className="bg-white p-6 rounded-2xl shadow-sm h-full flex flex-col">
                <Loader />
            </main>
        );
    }

    return (
        <main className="bg-white p-6 rounded-2xl shadow-sm h-full flex flex-col">
            <div className="flex-shrink-0">
                <div className="flex items-center gap-3">
                    <div className="bg-gray-100 h-8 w-8 flex items-center justify-center rounded-full shadow-sm">
                        <FiUsers className="h-4 w-4 mph-primary" />
                    </div>
                    <h2 className="text-xl font-bold mph-primary">
                        Matching candidates
                    </h2>
                    {/* {candidates?.length > 0 && (
                        <span className="bg-gray-100 text-gray-666 h-7 w-8 text-xs font-semibold px-2.5 py-1 rounded-full">
                            {candidates.length}
                        </span>
                    )} */}
                </div>

                <div className="relative hidden mt-4">
                    <input
                        type="text"
                        placeholder="Refine candidates using a prompt (e.g., 'Show only those with site inspection experience and fluent in Hindi')"
                        className="w-full border rounded-lg py-3 pl-4 pr-12 text-xs placeholder:text-gray-999 focus:outline-none"
                    />
                    <button className="absolute right-2 top-1/2 -translate-y-1/2 bg-gradient text-white h-6 w-6 flex items-center justify-center rounded-lg">
                        <IoSparklesOutline size={14} />
                    </button>
                </div>

                <div className="flex items-center gap-6 mt-4">
                    {tabs.map((tab) => (
                        <FilterTab
                            key={tab.label}
                            label={tab.label}
                            count={tab.count}
                            isActive={activeTab === tab.label}
                            onClick={() => {
                                if (activeTab !== tab.label) {
                                    console.log("Switching to tab:", tab.label);
                                    setActiveTab(tab.label);
                                    setIsFirstTime(isFirstTime + 1);
                                }
                            }}
                            disabled={loadingTab}
                        />
                    ))}
                </div>

                {pageType === "matching" && (
                    <div className="flex justify-between items-center mt-4">
                        <div className="flex items-center gap-3">
                            {activeTab !== "Submitted" && (
                                <>
                                    <input
                                        type="checkbox"
                                        className={`h-3.5 w-3.5 hidden rounded border-2 ${
                                            selectAll
                                                ? "border-mph-primary accent-[#004645]"
                                                : "border-gray-300"
                                        }`}
                                        checked={selectAll}
                                        onChange={(e) => setSelectAll(e.target.checked)}
                                        style={selectAll ? { accentColor: "#004645" } : {}}
                                    />
                                    <label className="text-sm hidden font-semibold text-gray-333">
                                        Select all
                                    </label>
                                </>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button className="hidden gap-1 px-2 h-8 text-xs flex items-center justify-center border font-semibold border-gray-200 rounded-full text-gray-333 hover:bg-gray-100">
                                <FiBookmark className="mph-primary" /> Save search
                            </button>
                            <button className="hidden h-8 w-8 flex items-center justify-center border border-gray-200 rounded-full text-gray-500 hover:bg-gray-100">
                                <FiMail size={16} className="mph-primary" />
                            </button>
                            <button className="hidden h-8 w-8 flex items-center justify-center border border-gray-200 rounded-full text-green-500 hover:bg-gray-100">
                                <FaWhatsapp size={18} />
                            </button>
                            {activeTab !== "Submitted" && (
                                <button
                                    className={`flex hidden items-center gap-2 text-xs font-semibold border px-4 py-1.5 rounded-full bg-gradient text-white hover:bg-gradient ${
                                        !selectAll || submitting
                                            ? "opacity-50 cursor-not-allowed"
                                            : ""
                                    }`}
                                    onClick={handleSubmitTaged}
                                    disabled={!selectAll || submitting}
                                >
                                    {submitting && (
                                        <svg
                                            className="animate-spin h-4 w-4 text-white"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                                fill="none"
                                            />
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                            />
                                        </svg>
                                    )}
                                    {submitting ? "Submitting..." : "Submit all to AM"}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div
                className="flex-1 mt-4 pt-2 space-y-4 pr-2 overflow-hidden"
                style={{
                    overflowY: "auto",
                    maxHeight: "calc(100vh - 297px)",
                    minHeight: 0,
                }}
            >
                {loadingTab ? (
                    <div className="flex justify-center items-center h-full">
                        <Loader />
                    </div>
                ) : candidatesToShow.length === 0 ? (
                    // <div className="text-center text-gray-400 text-sm mt-8">
                    //     No candidates found for this job.
                    // </div>
                    <div className="flex flex-col items-center justify-center text-gray-400 text-sm mt-8">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-10 h-10 mb-2 text-gray-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                    >
                        <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 17v-6h13M9 11l3-3m0 0l3 3m-3-3v12M3 3h6v6H3z"
                        />
                    </svg>

                    <p className="text-gray-500">No results available</p>
                    <p className="text-xs text-gray-400 mt-1">
                        We couldn't find any candidates for this job.
                    </p>
                    </div>
                ) : (
                    candidatesToShow.map((c, idx) => (
                        <CandidateItem
                            key={`${c.id}-clone-${idx}`}
                            candidate={c}
                            pageType={pageType}
                            disableSubmitToAM={selectAll}
                            candidateList={candidatesToShow}
                            onMatchingCandidateClick={() =>
                                handleMatchingCandidateClick(c)
                            }
                            query={query}
                            searchType={searchType}
                            mph_jobid={mph_jobid}
                            job_id={job_id}
                            jobDetails={jobDetails}
                            onTagAction={handleTagAction}
                            onSubmitCandidate={handleSubmission}
                            onUpdateCandidate={onUpdateCandidate}
                            onCandidateUpdateFilter={(updatedCandidate) => {
                                setCandidates(prev => prev.map(c => c.id === updatedCandidate.id ? updatedCandidate : c));
                                onUpdateCandidate?.(updatedCandidate);
                            }}
                            isLastIndex={candidatesToShow?.length >1 && idx >= candidatesToShow?.length - 1}
                        />
                    ))
                )}
            </div>
        </main>
    );
};

export default MatchingCandidatesList;